import { isEmpty } from 'lodash-es';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { deleteCampaign } from '~/src/dash/api/console-api';
import { EBool } from '~/src/dash/api/types/bool';
import {
  getLineItemsList,
  getLineItemsListRawNoPagination,
  ISimpleLineItem,
  ISimpleLineItemRaw,
  TLineItemListOrderByKey,
  updateLineItemActiveStatus,
} from '~/src/dash/apiV2/campaigns';
import { IStartToEndDateRanges } from '~/src/dash/components/calendar/common';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { formatSelectedFiltersIntoQueryObject } from '~/src/dash/components/filters/conversions';
import { DateRangeRow } from '~/src/dash/components/filters/DateRangeRow';
import { FilterRowV2 } from '~/src/dash/components/filters/FilterRowV2';
import { EDebugPrebidData } from '~/src/dash/components/filters/options/campaigns';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { SearchResultsText } from '~/src/dash/components/input/common';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractDefaultActiveColumnKeys,
  extractVisibleColumns,
  IListViewTableColumnInfo,
  IListViewTableRow,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { ColumnSelector } from '~/src/dash/components/tables/ListViewTable/linked_components/ColumnSelector';
import {
  ButtonV2,
  Col,
  Cols,
  Panel,
  PanelBreak,
  Panels,
  Row,
  Rows,
  spacing,
  Text,
  TextInput,
  TooltipWithTranslation,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  RouteCampaignCopy,
  RouteCampaignExisting,
  RouteCampaignNew,
} from '~/src/dash/router/routes';
import { isSameMoment } from '~/src/dash/utils/calendar';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { useBaseConfig } from '~/src/dash/views/base/contexts';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { formatLineItemsResponseIntoLVTRows } from '~/src/dash/views/campaign/OrderLineItemsListView/config';
import { OrderLineItemsListContext } from '~/src/dash/views/campaign/OrderLineItemsListView/contexts';
import { BulkUploadMenu } from '~/src/dash/views/campaigns/line-items/LineItemsListView/BulkUploadMenu';
import { validateDateRanges } from '~/src/dash/components/calendar/common';
import {
  ExportLineItemsCSVButton,
  formatLineItemsDataToCSV,
  generateLineItemsCSVTitle,
} from '~/src/dash/views/campaigns/line-items/LineItemsListView/ExportLineItemsCSVButton';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

export const OrderLineItemsListView = ({
  orderID,
  getOrder,
  refresh,
}: {
  orderID: number;
  getOrder: () => void;
  refresh: number;
}): React.ReactNode => {
  const { session, user, isSuperUser, isDemoUser } = useBaseStore();
  const { t } = usePublicaTranslation();
  const { Campaigns } = useBaseConfig();
  const cfg = Campaigns.LineItems.OrderLineItems;
  const readAuthorization = IS_AUTHORIZED(['read_campaign'], user.user);
  const writeAuthorization = IS_AUTHORIZED(['write_campaign'], user.user);

  const isMounted = useRef(false);

  const [startToEndDateRanges, setStartToEndDateRanges] = useState<IStartToEndDateRanges>(
    cfg.startToEndDateRanges,
  );
  const [areDateRangesInvalid, setAreDateRangesInvalid] = useState(false);

  const [filtersLoadState, setFiltersLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [filters, setFilters] = useState<Record<string, ISelectOption[]>>({});
  const [selectedFilters, setSelectedFilters] = useState<Record<string, Set<string | number>>>({});
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    cfg.timezone || session.publisher.Timezone,
  );

  const [activeColumnKeys, setActiveColumnKeys] = useState<string[]>(
    cfg.activeColumnKeys || extractDefaultActiveColumnKeys(cfg.columns),
  );

  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [responseData, setResponseData] = useState<ISimpleLineItem[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [isTableActionPending, setIsTableActionPending] = useState(false);

  const [query, setQuery] = useState('');

  const [order, setOrder] = useState<Order>(cfg.table.order || 'asc');
  const [orderBy, setOrderBy] = useState<TLineItemListOrderByKey>(
    (cfg.table.orderBy as TLineItemListOrderByKey) || 'name',
  );
  const [page, setPage] = useState<number>(cfg.table.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(cfg.table.rowsPerPage || 10);

  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);

  const populateFilters = (): void => {
    setFiltersLoadState(LoadingStateLoading);
    const keys: string[] = [];
    const getListFns: Array<Promise<ISelectOption[]>> = [];

    Object.values(cfg.filters).forEach((filter) => {
      keys.push(filter.key);
      getListFns.push(filter.getList(session, user));
    });

    const tempFilters: Record<string, ISelectOption[]> = {};
    const tempSelectedFilters: Record<string, Set<string | number>> = {};

    Promise.all(getListFns)
      .then((results) => {
        results.forEach((result, index) => {
          const filterKey = keys[index];
          tempFilters[filterKey] = result;
          tempSelectedFilters[filterKey] =
            cfg.filters[filterKey].selectedOptionIDs || new Set<string | number>();
        });
        unstable_batchedUpdates(() => {
          setFilters(tempFilters);
          setFiltersLoadState(LoadingStateHasData);
          setSelectedFilters(tempSelectedFilters);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setFilters({});
          setSelectedFilters({});
          setFiltersLoadState(LoadingStateNoData);
          setSnackbarError(true);
          setSnackbarMessage(t('SnackbarErrorRetrievingItem', { item: t('Filters') }));
        });
      });
  };

  const getData = (): void => {
    setLoadState(LoadingStateLoading);
    const filterParams = formatSelectedFiltersIntoQueryObject(selectedFilters, cfg.filters);
    void getLineItemsList(session.publisher.ID, {
      publisher_id: session.publisher.ID,
      search: query,
      page_size: rowsPerPage,
      page,
      order_direction: order,
      order_by: orderBy,
      active: EBool.EBool_TRUE,
      status: [],
      timezone: selectedTimezone,
      start_date_from: startToEndDateRanges.startDateFrom?.format('YYYY-MM-DD') || '',
      start_date_to: startToEndDateRanges.startDateTo?.format('YYYY-MM-DD') || '',
      end_date_from: startToEndDateRanges.endDateFrom?.format('YYYY-MM-DD') || '',
      end_date_to: startToEndDateRanges.endDateTo?.format('YYYY-MM-DD') || '',
      debug: EDebugPrebidData.ShowAll,
      advertiser_ids: [],
      order_ids: [orderID],
      ...filterParams,
    })
      .then(({ campaigns, totalCount }) => {
        unstable_batchedUpdates(() => {
          setResponseData(campaigns);
          setTotalRowCount(totalCount);
          setLoadState(LoadingStateHasData);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setResponseData([]);
          setTotalRowCount(0);
          setLoadState(LoadingStateNoData);
          setSnackbarError(true);
          setSnackbarMessage(t('SnackbarErrorRetrievingItem', { item: t('LineItems') }));
        });
      });
  };

  const getCSVData = async (): Promise<ISimpleLineItemRaw[]> => {
    const filterParams = formatSelectedFiltersIntoQueryObject(selectedFilters, cfg.filters);
    const { campaigns } = await getLineItemsListRawNoPagination(session.publisher.ID, {
      publisher_id: session.publisher.ID,
      search: query,
      page_size: rowsPerPage,
      page,
      order_direction: order,
      order_by: orderBy,
      active: EBool.EBool_TRUE,
      status: [],
      timezone: selectedTimezone,
      start_date_from: startToEndDateRanges.startDateFrom?.format('YYYY-MM-DD') || '',
      start_date_to: startToEndDateRanges.startDateTo?.format('YYYY-MM-DD') || '',
      end_date_from: startToEndDateRanges.endDateFrom?.format('YYYY-MM-DD') || '',
      end_date_to: startToEndDateRanges.endDateTo?.format('YYYY-MM-DD') || '',
      debug: EDebugPrebidData.ShowAll,
      advertiser_ids: [],
      order_ids: [orderID],
      ...filterParams,
    });
    return campaigns;
  };

  const handleApplyDateRanges = (ranges: IStartToEndDateRanges): void => {
    const { startDateFrom, startDateTo, endDateFrom, endDateTo } = ranges;
    // Only update state if the values are different
    const hasChanged =
      !isSameMoment(startToEndDateRanges.startDateFrom, startDateFrom) ||
      !isSameMoment(startToEndDateRanges.startDateTo, startDateTo) ||
      !isSameMoment(startToEndDateRanges.endDateFrom, endDateFrom) ||
      !isSameMoment(startToEndDateRanges.endDateTo, endDateTo);

    if (hasChanged) {
      // validate date ranges
      const { isValid, errorTKey, errorTProps } = validateDateRanges(ranges);
      if (!isValid) {
        unstable_batchedUpdates(() => {
          setSnackbarMessage(t(errorTKey, errorTProps));
          setSnackbarError(true);
          setAreDateRangesInvalid(true);
        });
      } else {
        unstable_batchedUpdates(() => {
          setStartToEndDateRanges(ranges);
          setAreDateRangesInvalid(false);
        });
      }
    } else if (!startDateFrom && !startDateTo && !endDateFrom && !endDateTo) {
      setAreDateRangesInvalid(false);
    }
  };

  const handleFilterAccept = (value: Set<number | string>, key: string): void => {
    if (
      selectedFilters[key].size === value.size &&
      [...selectedFilters[key]].every((item) => value.has(item))
    ) {
      // No change, do nothing
      return;
    } else {
      const clonedSelectedFilters = { ...selectedFilters };
      clonedSelectedFilters[key] = value;
      unstable_batchedUpdates(() => {
        setSelectedFilters(clonedSelectedFilters);
        setPage(0);
      });
      cfg.table.page = 0;
    }
  };

  const handleClearFilters = (): void => {
    const clearedSelectedFilters: Record<string, Set<string | number>> = {};
    Object.values(cfg.filters).forEach((filter) => {
      const resetValue = new Set<string | number>(filter.selectedOptionIDsOnReset || []);
      clearedSelectedFilters[filter.key] = resetValue;
    });
    unstable_batchedUpdates(() => {
      setSelectedFilters(clearedSelectedFilters);
      setPage(0);
    });
    cfg.table.page = 0;
  };

  const handleApplyTimezone = (timezone: Set<number | string>): void => {
    setSelectedTimezone(timezone.values().next().value as string);
  };

  const handleOnChangeQuery = (text: string): void => {
    setQuery(text);
  };

  const handleChangeActiveColumns = (keys: string[]): void => {
    setActiveColumnKeys(keys);
  };

  const handleAcceptActiveColumns = (keys: string[]): void => {
    setActiveColumnKeys(keys);
  };

  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo): void => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key as TLineItemListOrderByKey);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleOnChangeStatus = (id: string, checked: boolean, row: IListViewTableRow): void => {
    setIsTableActionPending(true);
    updateLineItemActiveStatus(session.publisher.ID, id, {
      publisher_id: session.publisher.ID,
      id: Number(id),
      active: checked ? EBool.EBool_TRUE : EBool.EBool_FALSE,
    })
      .then(() => {
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage(
            t('SnackbarSuccessfullyUpdatedIDName', {
              id: String(id),
              name: (row?.Name?.value as string) || t('LineItem'),
            }),
          );
          setSnackbarError(false);
        });
        getOrder();
        getData();
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage(
            t('SnackbarFailedToUpdateIDName', {
              id: String(id),
              name: (row?.Name?.value as string) || t('LineItem'),
            }),
          );
          setSnackbarError(true);
        });
      });
  };

  const getLineItemSettingsRoute = (id: string): string => {
    return getRoutePath(RouteCampaignExisting, extractRouteParams({ session, user }), {
      c: id,
      order: responseData.find((item) => item.ID.toString() === id)?.OrderID.toString(),
    });
  };

  const handleCloneLineItem = (id: string): void => {
    navigate(RouteCampaignCopy, extractRouteParams({ session, user }), {
      c: id,
      order: responseData.find((item) => item.ID.toString() === id)?.OrderID.toString(),
    });
  };

  const handleDeleteLineItem = (id: string): Promise<unknown> => {
    setIsTableActionPending(true);
    deleteCampaign({
      query: {
        publisher_id: session.publisher.ID,
        campaign_id: id,
      },
    })
      .then(() => {
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage(t('SnackbarSuccessfullyDeletedItem', { item: t('LineItem') }));
          setSnackbarError(false);
        });
        getData();
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage(t('SnackbarFailedToDeleteItem', { item: t('LineItem') }));
          setSnackbarError(true);
        });
      });
    return new Promise((resolve) => resolve(undefined));
  };

  const handleChangePage = (_e: unknown, newPage: number): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(updatedRowsPerPage);
      setPage(0);
    });
    cfg.table.page = 0;
  };

  const handleCreateLineItems = (): void => {
    navigate(RouteCampaignNew, extractRouteParams({ session, user }), {
      order: orderID.toString(),
    });
  };

  useEffect(() => {
    populateFilters();
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      getData();
    }
  }, [
    query,
    order,
    orderBy,
    page,
    rowsPerPage,
    startToEndDateRanges,
    selectedFilters,
    refresh,
    selectedTimezone,
  ]);

  useEffect(() => {
    isMounted.current = true;
  }, []);

  const activeColumnsList = useMemo(
    () => extractVisibleColumns(cfg.columns, activeColumnKeys, isSuperUser),
    [],
  );
  const rows = formatLineItemsResponseIntoLVTRows(responseData, cfg.columns);

  return (
    <OrderLineItemsListContext.Provider value={{ cfg, readAuthorization, writeAuthorization }}>
      <Panels
        max
        vertical
        relative
        minWidthMain
        largeHorizontalPadding
        style={{ rowGap: spacing.base }}
      >
        <Panel>
          <Cols
            wrap
            gap={spacing.small}
            style={{
              justifyContent: 'flex-end',
              alignItems: 'center',
              rowGap: spacing.small,
            }}
          >
            <Col>
              <BulkUploadMenu handleSuccess={() => getData()} />
            </Col>
            <Col>
              {orderID !== undefined && (
                // if on new order without saving
                <TooltipWithTranslation
                  tKey={orderID === 0 ? 'DisableAddLineItemTooltip' : 'AddItem'}
                  tProps={{ item: t('LineItem') }}
                >
                  <div>
                    <ButtonV2 onClick={handleCreateLineItems} disabled={orderID === 0}>
                      <Cols
                        gap={spacing.small}
                        style={{
                          alignItems: 'center',
                        }}
                      >
                        <Col displayFlex>
                          <IconV2
                            name='add'
                            color={orderID === 0 ? publicaColors.gray : publicaColors.white}
                          />
                        </Col>
                        <Col>
                          <Text
                            inheritColor
                            inheritWeight
                            tKey='AddItem'
                            tProps={{ item: t('LineItem') }}
                          />
                        </Col>
                      </Cols>
                    </ButtonV2>
                  </div>
                </TooltipWithTranslation>
              )}
            </Col>
          </Cols>
        </Panel>
        <Panel highlighted>
          <Rows>
            <Row noPadding style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}>
              <DateRangeRow
                cypressParentName={cfg.cypressClassName}
                selectedRanges={startToEndDateRanges}
                handleApplyDateRanges={handleApplyDateRanges}
                hasError={areDateRangesInvalid}
                defaultTimezone={selectedTimezone}
                handleApplyTimezone={handleApplyTimezone}
              />
            </Row>
            <Row noPadding>
              <FilterRowV2
                cypressParentName={cfg.cypressClassName}
                enableClear
                filtersConfig={cfg.filters}
                filtersToOptionsMap={filters}
                handleClearFilters={handleClearFilters}
                handleApplySelect={handleFilterAccept}
                selectedFilters={selectedFilters}
                loadState={filtersLoadState}
              />
            </Row>
          </Rows>
        </Panel>
        <Panel highlighted largeHorizontalPadding verticalPadding>
          <Rows>
            <Row noPadding>
              <Cols style={{ justifyContent: 'space-between' }}>
                <Col>
                  <Cols
                    wrap
                    gap={spacing.medium}
                    style={{
                      alignItems: 'center',
                      rowGap: spacing.small,
                    }}
                  >
                    <Col>
                      <Cols
                        wrap
                        gap={spacing.medium}
                        style={{
                          alignItems: 'center',
                          rowGap: spacing.small,
                        }}
                      >
                        <Col>
                          <TextInput
                            enableClear
                            handleOnChange={handleOnChangeQuery}
                            msDelay={500}
                            placeholder={t('SearchByNameID')}
                            prefix='search'
                          />
                        </Col>
                      </Cols>
                    </Col>
                    <Col>
                      <SearchResultsText
                        numRowsDisplayed={responseData.length}
                        numTotalRows={totalRowCount}
                      />
                    </Col>
                  </Cols>
                </Col>
                <Col>
                  <Cols
                    wrap
                    gap={spacing.extraSmall}
                    style={{
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      rowGap: spacing.extraSmall,
                    }}
                  >
                    <Col>
                      <ExportLineItemsCSVButton
                        isDisabled={loadState !== LoadingStateHasData && !isEmpty(rows)}
                        data={async () => {
                          const data = await getCSVData();
                          return formatLineItemsDataToCSV(data);
                        }}
                        filename={'OrderLineItemsListView'}
                        title={generateLineItemsCSVTitle(
                          formatSelectedFiltersIntoQueryObject(selectedFilters, cfg.filters),
                          startToEndDateRanges,
                        )}
                      />
                    </Col>
                    <Col>
                      <ColumnSelector
                        preselectedColumnKeys={activeColumnKeys}
                        resetActiveColumnKeys={cfg.columnSelector.resetKeys}
                        columnsList={activeColumnsList}
                        columnsHandleOnChangeOption={handleChangeActiveColumns}
                        columnsHandleOnAccept={handleAcceptActiveColumns}
                      />
                    </Col>
                  </Cols>
                </Col>
              </Cols>
            </Row>
            <Row noHorizontalPadding verticalPadding>
              <ListViewTable
                activeColumnKeys={activeColumnKeys}
                columns={cfg.columns}
                displayNameWithID={!isDemoUser}
                enableActions
                enableStatusSwitch
                getItemSettingsRoute={getLineItemSettingsRoute}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                handleCloneItem={handleCloneLineItem}
                handleDeleteItem={handleDeleteLineItem}
                handleOnChangeStatus={handleOnChangeStatus}
                handleOnClickColumnHeader={handleOnClickColumnHeader}
                isActionPending={isTableActionPending}
                isReadAuthorized={readAuthorization.isAuthorized}
                isSuperUser={isSuperUser}
                isWriteAuthorized={writeAuthorization.isAuthorized}
                loadState={loadState}
                order={order}
                orderBy={orderBy}
                page={page}
                rows={rows}
                rowsPerPage={rowsPerPage}
                totalRowCount={totalRowCount}
                className={cfg.cypressClassName}
              />
            </Row>
          </Rows>
        </Panel>
        <SnackbarView
          open={!!snackbarMessage}
          error={!!snackbarError}
          onClose={() => setSnackbarMessage('')}
        >
          {snackbarMessage}
        </SnackbarView>
        <PanelBreak height={spacing.large} />
      </Panels>
    </OrderLineItemsListContext.Provider>
  );
};
